import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { createStyles, useTheme } from '@material-ui/styles';
import { Theme } from '@material-ui/core';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import BaseTypography from '../Text/BaseTypography';
import { RootState } from '../../store';
import { NonHoverBorder, SidebarColors } from '../../theme/colors';
import { FlagsContext } from '../../context';
import { useSidebarTheme } from '../Sidebar/useSidebarTheme';

interface NotificationBadgeStyleProps {
  isSidebar: boolean;
  isPreview: boolean;
  sidebarBadgeTextColor: string;
  sidebarBadgeBackground: string;
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      width: ({ isPreview }: NotificationBadgeStyleProps) =>
        isPreview ? '12px' : '18px',
      height: ({ isPreview }: NotificationBadgeStyleProps) =>
        isPreview ? '12px' : '18px',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: ({
        isSidebar,
        sidebarBadgeBackground,
      }: NotificationBadgeStyleProps) =>
        isSidebar ? sidebarBadgeBackground : theme.palette.primary.main,
      color: ({
        isSidebar,
        sidebarBadgeTextColor,
      }: NotificationBadgeStyleProps) =>
        isSidebar
          ? `${sidebarBadgeTextColor}`
          : `${theme.palette.primary.contrastText}`,
      '&:hover': {
        color: ({
          isSidebar,
          sidebarBadgeTextColor,
        }: NotificationBadgeStyleProps) =>
          isSidebar
            ? `${sidebarBadgeTextColor}`
            : `${theme.palette.primary.contrastText}`,
      },
    },
    oval: {
      borderRadius: '50%',
    },
    rectangle: {
      borderRadius: '2px',
      lineHeight: '19px',
      padding: 0,
    },
    hidden: {
      opacity: 0,
    },
  }),
);

interface NotificationBadgeProps {
  content: number;
  isSidebar?: boolean;
  showZero?: boolean;
  isPreview?: boolean;
  htmlId?: string;
  previewClass?: string;
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = React.memo(
  ({
    content,
    isSidebar = false,
    showZero = false,
    isPreview = false,
    htmlId,
    previewClass = '',
  }) => {
    const theme: Theme = useTheme();
    const { EnableAdvancedBranding } = React.useContext(FlagsContext);
    const { sidebarTheme } = useSidebarTheme();
    const isClient = useSelector((state: RootState) => state.user.isClient);

    // for advanced branding
    // background color for sidebar notification badge will be text color of sidebar
    // text color for sidebar notification badge will be sidebar background color
    const clientSidebarBadgeBackground = EnableAdvancedBranding
      ? sidebarTheme.sidebarTextColor
      : theme.palette.primary.contrastText;
    const clientSidebarBadgeTextColor = EnableAdvancedBranding
      ? sidebarTheme.sidebarBackgroundColor
      : theme.palette.primary.main;

    const classes = useStyles({
      isSidebar,
      isPreview,
      sidebarBadgeBackground: isClient // sidebar notification badge background color based on user type ( internal or client)
        ? clientSidebarBadgeBackground
        : NonHoverBorder,
      sidebarBadgeTextColor: isClient // sidebar notification badge text color based on user type ( internal or client)
        ? clientSidebarBadgeTextColor
        : SidebarColors.InternalSidebarText,
    });

    return (
      <span
        data-testid={htmlId}
        className={classNames(classes.root, {
          [previewClass]: isPreview, // on customization page for client view preivew use previewClass
          [classes.oval]: isSidebar,
          [classes.rectangle]: !isSidebar,
          [classes.hidden]: content < 1 && !showZero,
        })}
      >
        {!isPreview ? (
          <BaseTypography fontType="9Regular" component="span">
            {content}
          </BaseTypography>
        ) : (
          <BaseTypography
            fontType="9Regular"
            style={{ fontSize: 6 }}
            component="span"
          >
            {content}
          </BaseTypography>
        )}
      </span>
    );
  },
);
